import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { useShadowRoot } from './ShadowRootContext';
import { cn } from './cn';

interface SelectContextValue {
  hasError: boolean;
}

const SelectContext = React.createContext<SelectContextValue>({
  hasError: false,
});

const Select = ({
  hasError = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  hasError?: boolean;
}) => {
  const contextValue = React.useMemo(() => ({ hasError }), [hasError]);

  return (
    <SelectContext.Provider value={contextValue}>
      <SelectPrimitive.Root {...props} />
    </SelectContext.Provider>
  );
};

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const { hasError } = React.useContext(SelectContext);
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary ring-offset-bg-secondary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-border-accent-quaternary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        hasError &&
          'border-border-negative-strong text-text-negative focus:ring-border-negative-strong',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    usesPortal?: boolean;
  }
>(({ className, children, position = 'popper', usesPortal, ...props }, ref) => {
  const { container } = useShadowRoot();
  const content = (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-bg-primary text-text-primary shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      collisionBoundary={container}
      collisionPadding={container ? { top: 50 } : undefined}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] max-w-[calc(100cqw-2rem)]',
        )}
      >
        <div className="max-h-[calc(min(var(--radix-popper-available-height),480px)-0.5rem)] overflow-auto px-1">
          {children}
        </div>
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  );

  // TODO(david)[ENG-296]: Remove this once Radix UI dependencies are fixed.
  if (usesPortal === false) {
    return content;
  }

  return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => {
  const { hasError } = React.useContext(SelectContext);
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        'py-1.5 pl-8 pr-2 text-sm font-semibold',
        hasError && 'text-text-negative-strong',
        className,
      )}
      {...props}
    />
  );
});

SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-border-accent-primary focus:text-text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-bg-quaternary', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContext,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
